import { api } from '../core';
import { MEDICAL_MATCH_TAG } from '../constants';

// Types for the Medical Match API response
export interface MedicalMatchPageInfo {
  fullBundlePageNumber: number;
  pageId: string;
}

export interface MedicalMatchPage {
  pageInfo: MedicalMatchPageInfo;
  snippet: string;
}

/**
 * How the question behind an entry came to exist:
 *   - STANDARD — part of the default question set every case is run against
 *   - CUSTOM   — chosen by the user at case-creation time
 *   - LIVE     — asked ad-hoc from the Medical Match panel while reviewing
 */
export enum MedicalMatchCreationType {
  LIVE = 'LIVE',
  STANDARD = 'STANDARD',
  CUSTOM = 'CUSTOM',
}

export interface MedicalMatchEntry {
  answer: string;
  creationType: MedicalMatchCreationType;
  /** The live question that produced this entry; null for the standard set. */
  customQuestionId: string | null;
  entryId: string;
  pages: MedicalMatchPage[];
  question: string;
}

export interface MedicalMatchReference {
  pageName: string;
  snippet: string;
}

export interface MedicalMatchBreach {
  breach: string;
  references: MedicalMatchReference[];
}

export interface MedicalMatchItem {
  allegation?: string;
  breaches: MedicalMatchBreach[];
  entries: MedicalMatchEntry[];
  title: string;
}

export type MedicalMatchResponse = MedicalMatchItem[];

export interface ExcludedDocument {
  documentId: string;
  documentName: string;
}

interface RawMedicalMatchResponse {
  groups: MedicalMatchItem[];
  excludedDocuments: ExcludedDocument[];
  // Entries whose answer is out of date because the bundle changed after the
  // MedMatch run, keyed by entry id. Nothing in the panel reads it yet, so the
  // value shape is left opaque rather than guessed at.
  staleEntries: Record<string, unknown>;
}

interface GetMedicalMatchProps {
  caseId: string;
}

const getMedicalMatchApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getMedicalMatch: builder.query<MedicalMatchResponse, GetMedicalMatchProps>({
      query: ({ caseId }: GetMedicalMatchProps) => ({
        url: `/medical-match?caseId=${encodeURIComponent(caseId)}`,
        method: 'GET',
      }),
      transformResponse: (response: RawMedicalMatchResponse) => response.groups,
      providesTags: (_result, _err, { caseId }) => [{ type: MEDICAL_MATCH_TAG, id: caseId }],
      extraOptions: { maxRetries: 3 },
    }),
  }),
});

/**
 * Export hooks for usage in functional components, which are
 * auto-generated based on the defined endpoints
 */
export const { useGetMedicalMatchQuery, useLazyGetMedicalMatchQuery } = getMedicalMatchApi;
